import { Inject, Injectable } from "@angular/core";
import { map, shareReplay, Subject } from "rxjs";
import { ThemeObj, availableThemes, Theme, LocalStorageKey, ThemeVar, AppViewVar } from "@model";
import { LocalStorageService } from "../persistance";
import { DOCUMENT } from "@angular/common";
import { isNotNullOrUndefined } from "@helpers";

@Injectable({ providedIn: "root" })
export class ThemesService {
  readonly availableThemes = availableThemes;

  private _currentThemeObj$ = new Subject<ThemeObj>();
  currentThemeObj$ = this._currentThemeObj$.asObservable();

  currentTheme$ = this._currentThemeObj$.pipe(
    isNotNullOrUndefined(),
    shareReplay(),
    map((currentThemeObj) => currentThemeObj.id),
  );

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _localStorageService: LocalStorageService,
  ) {}

  initialSetupTheme(): void {
    const theme = this._localStorageService.get(LocalStorageKey.THEME);
    if (theme && Object.values(Theme).includes(theme as Theme)) {
      this.switchTheme(theme as Theme);
    } else {
      this.switchTheme(this.availableThemes[0].id);
    }
  }

  switchTheme(theme: Theme): void {
    this._setCurrentTheme(theme);
  }

  private async _setCurrentTheme(theme: Theme): Promise<void> {
    const newTheme = theme
      ? this.availableThemes.find((t) => t.id === theme)!
      : this.availableThemes[0];
    await this._loadStyle(newTheme.id);
    this._currentThemeObj$.next(newTheme);
    this._localStorageService.set(LocalStorageKey.THEME, newTheme.id);
  }

  private _loadStyle(themeId: Theme): Promise<HTMLLinkElement> {
    const src = `/assets/css-themes/${themeId}.css`;

    return new Promise(function (resolve, reject) {
      const timestamp = String(new Date().getTime());
      const link = document.createElement("link");
      link.setAttribute("timestamp", timestamp);
      link.setAttribute("contains", "theme");
      link.href = src;
      link.rel = "stylesheet";
      link.onload = () => {
        document.querySelectorAll('[contains="theme"]').forEach((styles) => {
          if (styles.getAttribute("timestamp") !== timestamp) {
            styles.remove();
          }
        });
        resolve(link);
      };

      link.onerror = () => {
        reject(new Error(`Style load error for ${src}`));
      };
      document.head.append(link);
    });
  }

  getThemeColor(ThemeVar: ThemeVar): string {
    const bodyElement = this._document.querySelector("body") as Element;
    return getComputedStyle(bodyElement).getPropertyValue(ThemeVar)?.trim() ?? "#ffffff";
  }

  // TODO: belongs here?
  getAppViewColor(appViewVar: AppViewVar): string {
    const appViewElement = this._document.querySelector("app-root") as Element;
    return getComputedStyle(appViewElement).getPropertyValue(appViewVar)?.trim() ?? "#ffffff";
  }
}
